import React, { useState, useEffect } from "react";
import { addSavingsGoal, updateSavingsGoal } from "../../services/Api";
import { useToast } from '../../services/ToastContext';
import PopupWrapper from "../../components/PopupWrapper";
import { useRefresh } from "../../services/RefreshContext";
import { useAuth } from "../../services/AuthContext";

const SavingsGoalForm = ({ isOpen, onClose, goalToEdit }) => {
  const { showToast } = useToast();
  const { triggerRefresh } = useRefresh();
  const { childID } = useAuth();

  const [goalName, setGoalName] = useState("");
  const [goalAmount, setGoalAmount] = useState("");
  const [goalID, setGoalID] = useState("");
  const [editGoal, setEditGoal] = useState(false);
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (goalToEdit) {
      setGoalName(goalToEdit.name);
      setGoalAmount(goalToEdit.goalAmount);
      setGoalID(goalToEdit.id);
      setEditGoal(true);
    } else {
      // Reset form when opened without edit data
      setGoalName("");
      setGoalAmount("");
      setGoalID("");
      setEditGoal(false);
    }
  }, [goalToEdit, isOpen]);

  const [inputErrors, setInputErrors] = useState({
    inputGoalName: "",
    inputGoalAmount: "",
  });

  // Validation function
  const validateAddSavingsGoalForm = () => {
    let valid = true;
    const newErrors = {
      inputGoalName: "",
      inputGoalAmount: "",
    };

    if (!goalName.trim()) {
      newErrors.inputGoalName = "Goal name is required";
      valid = false;
    }

    if (!goalAmount || isNaN(goalAmount) || parseFloat(goalAmount) <= 0) {
      newErrors.inputGoalAmount = "Enter a valid goal amount greater than 0";
      valid = false;
    }

    setInputErrors(newErrors);
    return valid;
  };

  const handleCancelButton = (e) => {
    e.preventDefault();
    onClose();
  };

  const handleAddSavingsGoal = async (e) => {
    e.preventDefault();
    setError("");
    setIsLoading(true);

    if (!validateAddSavingsGoalForm()) {
      setIsLoading(false);
      return;
    }

    try {
      // Check if it should be an edit or an addition of a goal
      if (editGoal) {
        await updateSavingsGoal(goalID, goalName, goalAmount);
        showToast("Goal Updated");
      } else {
        await addSavingsGoal(goalName, goalAmount, childID);
        showToast("Goal Added");
      }

      // Reset form fields
      setGoalName("");
      setGoalAmount("");
      
      triggerRefresh();
      onClose();
    } catch (error) {
      setError(error.message || "An error occurred while saving the goal.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <PopupWrapper
      isOpen={isOpen}
      onClose={onClose}
      title={editGoal ? "Edit Savings Goal" : "Add Savings Goal"}
    >
      <form className="user m-1">
        <div className="form-group">
          <input
            type="text"
            className="form-control form-control-user"
            id="goalNameInputAddSavingGoal"
            placeholder="Goal Name"
            onChange={(e) => setGoalName(e.target.value)}
            value={goalName}
          />
          {inputErrors.inputGoalName && (
            <p className="text-danger">{inputErrors.inputGoalName}</p>
          )}
        </div>
        <div className="form-group">
          <input
            type="number"
            className="form-control form-control-user"
            id="exampleInputAmount"
            placeholder="Amount Needed to Save"
            min="0"
            step="0.01"
            onChange={(e) => setGoalAmount(e.target.value)}
            value={goalAmount}
          />
          {inputErrors.inputGoalAmount && (
            <p className="text-danger">
              {inputErrors.inputGoalAmount}
            </p>
          )}
        </div>
        <br></br>
        {error && <p className="text-danger">{error}</p>}

        <div className="popupButtons">
          <button
            type="button"
            className="cancel-btn p-3 grey-color"
            onClick={handleCancelButton}
          >
            Cancel
          </button>

          <button
            type="submit"
            className={`confirm-btn p-3${isLoading ? " opacity-50 cursor-not-allowed" : ""}`}
            disabled={isLoading}
            onClick={handleAddSavingsGoal}
          >
            {isLoading
              ? editGoal
                ? "Updating Goal..."
                : "Adding Goal..."
              : editGoal
                ? "Update Goal"
                : "Add Goal"}
          </button>
        </div>
      </form>
    </PopupWrapper>
  );
};

export default SavingsGoalForm;